import type { ReactNode, JSX } from 'react'

import { AlertDialog, Button, SimpleSpinner } from '@buffer-mono/popcorn'

import type { DismissActionType } from './types'
import { useResolveCommentsDialog } from './useResolveCommentsDialog'

type ActionConfig = {
  title: string
  fallbackButtonText: string
  fallbackDescription: string
  emptyDesc: string
  contextPhrase: string
}

const ACTION_CONFIG: Record<DismissActionType, ActionConfig> = {
  post: {
    title: 'Resolve Comments on This Post',
    fallbackButtonText: 'Resolve Comments',
    fallbackDescription:
      "All unanswered comments on this post will be marked as resolved. You will be able to access them using the 'status' filter.",
    emptyDesc: 'This post has no unanswered comments that could be resolved.',
    contextPhrase: 'on this post ',
  },
  'older-than-month': {
    title: 'Resolve Comments Older Than 1 Month',
    fallbackButtonText: 'Resolve Comments',
    fallbackDescription:
      "Comments older than 1 month will be marked as resolved. You will be able to access them using the 'status' filter.",
    emptyDesc:
      'You currently have no unanswered comments older than 1 month that could be resolved.',
    contextPhrase: 'older than 1 month ',
  },
  all: {
    title: 'Resolve All Comments',
    fallbackButtonText: 'Resolve All Comments',
    fallbackDescription:
      "All new comments will be marked as resolved. You will be able to access them using the 'status' filter.",
    emptyDesc:
      'You currently have no unanswered comments that could be resolved.',
    contextPhrase: '',
  },
}

type ResolveCommentsDialogProps = {
  channelIds: string[]
  postId?: string
  actionType: DismissActionType
  onHideTooltip?: () => void
  shouldOpen?: boolean
  onOpenChange?: (open: boolean) => void
  onResolveComments?: () => void
  onResolvingChange?: (isResolving: boolean) => void
}

export const ResolveCommentsDialog = ({
  channelIds,
  postId,
  actionType,
  onHideTooltip,
  shouldOpen = false,
  onOpenChange,
  onResolveComments,
  onResolvingChange,
}: ResolveCommentsDialogProps): JSX.Element | null => {
  const {
    isOpen,
    handleOpenChange,
    handleResolveComments,
    isResolving,
    commentCount,
    isLoadingCount,
    errorCount,
    effectiveLimit,
  } = useResolveCommentsDialog({
    channelIds,
    postId,
    actionType,
    shouldOpen,
    onOpenChange,
    onHideTooltip,
    onResolvingChange,
  })

  // Check if limit was reached: when total count >= effectiveLimit
  const limitReached =
    effectiveLimit !== null &&
    commentCount !== null &&
    commentCount >= effectiveLimit

  const displayCount = limitReached ? `+${effectiveLimit}` : commentCount

  const getDialogContent = (): {
    title: string
    description: ReactNode
    buttonText: string
    buttonDisabled: boolean
  } => {
    const cfg = ACTION_CONFIG[actionType]
    const pluralSuffix = commentCount === 1 ? '' : 's'

    if (isLoadingCount) {
      return {
        title: cfg.title,
        description: <SimpleSpinner aria-label="Loading comment count" />,
        buttonText: cfg.fallbackButtonText,
        buttonDisabled: true,
      }
    }

    if (errorCount) {
      return {
        title: cfg.title,
        description: cfg.fallbackDescription,
        buttonText: cfg.fallbackButtonText,
        buttonDisabled: isResolving,
      }
    }

    if (commentCount === 0) {
      return {
        title: cfg.title,
        description: cfg.emptyDesc,
        buttonText: 'Got It',
        buttonDisabled: false,
      }
    }

    return {
      title: cfg.title,
      description: (
        <>
          <output aria-live="polite" aria-atomic="true">
            <strong>
              {displayCount} comment{pluralSuffix}
            </strong>{' '}
            {cfg.contextPhrase}will be marked as resolved.{' '}
          </output>
          You will be able to access them using the &apos;status&apos; filter.
        </>
      ),
      buttonText: `Resolve ${displayCount} Comment${pluralSuffix}`,
      buttonDisabled: isResolving,
    }
  }

  const { title, description, buttonText, buttonDisabled } = getDialogContent()

  if (!isOpen) {
    return null
  }

  // For "all" action, don't wait for count to show dialog
  if (actionType === 'all' && (isLoadingCount || commentCount === null)) {
    // Still show dialog, but with loading state
    return (
      <AlertDialog open={true} onOpenChange={handleOpenChange}>
        <AlertDialog.Content>
          <AlertDialog.Title>{title}</AlertDialog.Title>
          <AlertDialog.Description>{description}</AlertDialog.Description>
          <AlertDialog.Actions>
            <AlertDialog.Cancel>
              <Button variant="tertiary">Cancel</Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button
                variant="critical"
                disabled={buttonDisabled}
                onClick={(): void => {
                  void handleResolveComments()
                  onResolveComments?.()
                }}
              >
                {buttonText}
              </Button>
            </AlertDialog.Action>
          </AlertDialog.Actions>
        </AlertDialog.Content>
      </AlertDialog>
    )
  }

  // For "older-than-month" and "post" actions, wait for count before showing dialog
  if (
    (actionType === 'older-than-month' || actionType === 'post') &&
    (isLoadingCount || (commentCount === null && !errorCount))
  ) {
    return null
  }

  return (
    <AlertDialog open={true} onOpenChange={handleOpenChange}>
      <AlertDialog.Content>
        <AlertDialog.Title>{title}</AlertDialog.Title>
        <AlertDialog.Description>{description}</AlertDialog.Description>
        <AlertDialog.Actions>
          {commentCount === 0 ? (
            <AlertDialog.Cancel>
              <Button variant="primary" onClick={() => handleOpenChange(false)}>
                Got It
              </Button>
            </AlertDialog.Cancel>
          ) : (
            <>
              <AlertDialog.Cancel>
                <Button variant="tertiary">Cancel</Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action>
                <Button
                  variant="critical"
                  disabled={buttonDisabled}
                  onClick={(): void => {
                    void handleResolveComments()
                    onResolveComments?.()
                  }}
                >
                  {buttonText}
                </Button>
              </AlertDialog.Action>
            </>
          )}
        </AlertDialog.Actions>
      </AlertDialog.Content>
    </AlertDialog>
  )
}
